import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight, Truck, ClipboardList, Rocket, GraduationCap, ShieldCheck,
  Handshake, Eye, BookOpen, Compass, Users, Star, Quote,
} from "lucide-react";
import { api, IMAGES } from "../lib/api";
import { Seo } from "../components/site/Seo";
import { Reveal, StaggerWords } from "../components/site/Reveal";
import { Counter } from "../components/site/Counter";
import { RouteLine, BackgroundGrid, NodeField } from "../components/site/RouteLine";
import { Marquee, LightStreaks, Aurora } from "../components/site/Animations";
import { SectionHeading } from "../components/site/ServiceHero";
import { CTABand } from "../components/site/CTABand";
import { PILLARS, VALUES, JOURNEY, STEPS } from "../data/content";

const SERVICE_ICONS = { dispatch: Truck, hr: ClipboardList, startup: Rocket, training: GraduationCap, audit: ShieldCheck };
const SERVICE_LINKS = {
  dispatch: "/dispatch", hr: "/hr-back-office", startup: "/start-a-trucking-company",
  training: "/dispatch-class", audit: "/safety-audit",
};
const PILLAR_ICONS = [Users, BookOpen, Eye, Compass, Handshake];

export default function Home() {
  const [services, setServices] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [openFaq, setOpenFaq] = useState(0);
  const [tIndex, setTIndex] = useState(0);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const heroTextY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);
  const MARQUEE = ["DISPATCH", "NEW AUTHORITY", "COMPLIANCE", "SAFETY AUDIT", "HR & BACK-OFFICE", "DISPATCH TRAINING", "LOAD BOARDS", "RATE NEGOTIATION", "BROKER RELATIONSHIPS", "FMCSA READY"];

  useEffect(() => {
    api.get("/content").then((r) => {
      setServices(r.data.services || []);
      setTestimonials(r.data.testimonials || []);
      setFaqs(r.data.faqs || []);
    }).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <Seo title="Trucking Compliance, Dispatch & Startup Consulting" description="Next Mile Compliance helps trucking companies start right, stay compliant, and keep moving — dispatch, HR & back-office, business startup, dispatch training, and FMCSA safety audit preparation." />

      {/* ===================== HERO ===================== */}
      <section ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden">
        <motion.div className="absolute inset-0" style={{ y: heroY }} initial={{ scale: 1.14 }} animate={{ scale: 1 }} transition={{ duration: 8, ease: "easeOut" }}>
          <video
            className="w-full h-full object-cover"
            autoPlay muted loop playsInline preload="auto"
            poster={IMAGES.heroTruck}
            aria-label="Highway driving footage"
          >
            <source src={IMAGES.heroVideo} type="video/mp4" />
            <source src={IMAGES.heroVideoAlt} type="video/mp4" />
            <img src={IMAGES.heroTruck} alt="Semi truck on the highway at sunset" className="w-full h-full object-cover" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-r from-[#060A17] via-[#060A17]/90 to-[#060A17]/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#060A17] via-transparent to-[#060A17]/60" />
        </motion.div>
        <BackgroundGrid className="opacity-40" />
        <NodeField />
        <LightStreaks />

        <motion.div style={{ y: heroTextY }} className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-28 pb-16">
          <Reveal>
            <div className="inline-flex items-center gap-2 nm-card rounded-full px-4 py-2 mb-7">
              <span className="w-2 h-2 rounded-full bg-[#10B981] pulse-glow" />
              <span className="text-[11px] font-semibold tracking-wider text-gray-200">SERVING NEW AUTHORITIES & ESTABLISHED CARRIERS</span>
            </div>
          </Reveal>

          <h1 className="font-head text-5xl sm:text-6xl lg:text-7xl font-black leading-[0.95] text-white">
            {["START RIGHT.", "STAY COMPLIANT.", "KEEP MOVING."].map((line, i) => (
              <motion.span key={line} className="block"
                initial={{ opacity: 0, y: 40, filter: "blur(8px)" }}
                animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                transition={{ duration: 0.7, delay: 0.2 + i * 0.18, ease: [0.22, 1, 0.36, 1] }}>
                <span className={i === 2 ? "nm-gold-text" : ""}>{line}</span>
              </motion.span>
            ))}
          </h1>

          <Reveal delay={0.9}>
            <p className="mt-7 text-gray-300 text-base sm:text-lg max-w-2xl leading-relaxed">
              Helping trucking companies build the structure, systems, compliance, and support needed to operate with confidence.
              From starting your authority to dispatching your trucks, managing your back office, preparing for your safety audit,
              and teaching you how to run your own logistics business — Next Mile Compliance is here for every mile.
            </p>
          </Reveal>

          <Reveal delay={1.05}>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <Link to="/start-a-trucking-company"
                className="btn-sheen inline-flex items-center justify-center gap-2 nm-gold-bg text-[#0B132B] font-bold px-7 py-4 rounded-full hover:scale-105 transition-transform"
                data-testid="hero-start-btn">
                START YOUR TRUCKING COMPANY <ArrowRight size={18} />
              </Link>
              <a href="#services"
                className="inline-flex items-center justify-center gap-2 border border-white/20 text-white font-semibold px-7 py-4 rounded-full hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
                data-testid="hero-services-btn">
                EXPLORE OUR SERVICES
              </a>
            </div>
          </Reveal>
        </motion.div>

        <motion.div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-gray-400 flex flex-col items-center gap-2"
          animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }}>
          <span className="text-[10px] tracking-[0.3em] font-mono">SCROLL</span>
          <div className="w-[1px] h-10 bg-gradient-to-b from-[#D4AF37] to-transparent" />
        </motion.div>
      </section>

      {/* ===================== JOURNEY SEQUENCE ===================== */}
      <section className="relative py-14 bg-[#0B132B] border-y border-white/5 overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-wrap items-center justify-center gap-x-4 gap-y-3">
          {JOURNEY.map((w, i) => (
            <React.Fragment key={w}>
              <Reveal delay={i * 0.12}>
                <span className="font-head text-xl sm:text-3xl font-extrabold text-white/90">{w}</span>
              </Reveal>
              {i < JOURNEY.length - 1 && (
                <motion.span initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
                  transition={{ delay: i * 0.12 + 0.06 }}>
                  <ArrowRight className="text-[#D4AF37]" size={20} />
                </motion.span>
              )}
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* ===================== SERVICES ===================== */}
      <section id="services" className="relative py-24 overflow-hidden">
        <BackgroundGrid className="opacity-20" />
        <Aurora />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="What we do" center className="mb-14">
            EVERYTHING YOUR TRUCKING BUSINESS NEEDS — <span className="nm-gold-text">UNDER ONE ROOF.</span>
          </SectionHeading>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s, i) => {
              const Icon = SERVICE_ICONS[s.id] || Truck;
              return (
                <Reveal key={s.id} delay={i * 0.08}>
                  <motion.div whileHover={{ y: -8 }} transition={{ type: "spring", stiffness: 300 }}
                    className="group relative overflow-hidden nm-card rounded-2xl p-7 h-full flex flex-col hover:border-[#D4AF37]/60 hover:shadow-[0_20px_60px_-15px_rgba(212,175,55,0.25)] transition-all"
                    data-testid={`service-card-${s.id}`}>
                    <div className="absolute top-0 left-0 h-[3px] w-0 group-hover:w-full nm-gold-bg transition-all duration-500" />
                    <div className="flex items-center justify-between mb-6">
                      <span className="font-head text-5xl font-black text-white/10 group-hover:text-[#D4AF37]/25 transition-colors">{s.num}</span>
                      <div className="w-12 h-12 rounded-xl nm-gold-bg flex items-center justify-center">
                        <Icon className="text-[#0B132B]" size={22} />
                      </div>
                    </div>
                    <h3 className="font-head text-xl font-bold text-white mb-2">{s.title}</h3>
                    <p className="nm-gold-text font-head font-bold text-lg mb-3">{s.price}</p>
                    <p className="text-gray-400 text-sm leading-relaxed flex-1">{s.description}</p>
                    <Link to={SERVICE_LINKS[s.id] || "/contact"}
                      className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-white group-hover:text-[#D4AF37] transition-colors"
                      data-testid={`service-link-${s.id}`}>
                      Learn more <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </motion.div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===================== WHY US ===================== */}
      <section className="relative py-24 bg-[#0B132B] overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Why Next Mile Compliance" className="mb-14">
            WE DON'T JUST HELP YOU RUN TRUCKS. <span className="nm-gold-text">WE HELP YOU BUILD A BUSINESS.</span>
          </SectionHeading>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PILLARS.map((p, i) => {
              const Icon = PILLAR_ICONS[i];
              return (
                <Reveal key={p.title} delay={i * 0.08}>
                  <div className="nm-card rounded-2xl p-7 h-full hover:border-[#2563EB]/50 transition-colors" data-testid={`pillar-${i}`}>
                    <div className="w-11 h-11 rounded-lg bg-[#2563EB]/15 flex items-center justify-center mb-5">
                      <Icon className="text-[#3B82F6]" size={20} />
                    </div>
                    <h3 className="font-head text-base font-bold text-white mb-3 tracking-wide">{p.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{p.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <div className="mt-16 flex flex-wrap justify-center gap-x-6 gap-y-4">
            {VALUES.map((v, i) => (
              <motion.span key={v}
                initial={{ opacity: 0, scale: 0.8, y: 20 }} whileInView={{ opacity: 1, scale: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.15, duration: 0.6 }}
                className="font-display text-4xl sm:text-6xl text-white">
                {v}{i < VALUES.length - 1 && <span className="text-[#D4AF37] mx-2">.</span>}
              </motion.span>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== STATS ===================== */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.fleetAerial} alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-[#060A17]/85" />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { n: 10, s: "+", l: "Years Trucking Experience" },
            { n: 4, s: "+", l: "Years Hands-On Dispatch" },
            { n: 7, s: "", l: "Days a Week Support" },
            { n: 5, s: "", l: "Core Services" },
          ].map((st, i) => (
            <Reveal key={st.l} delay={i * 0.1}>
              <div className="font-head text-5xl sm:text-6xl font-black nm-gold-text">
                <Counter to={st.n} suffix={st.s} />
              </div>
              <p className="mt-2 text-gray-300 text-sm">{st.l}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ===================== HOW IT WORKS ===================== */}
      <section className="relative py-24 bg-[#0B132B] overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="How it works" center className="mb-16">
            FOUR STEPS TO YOUR <span className="nm-gold-text">NEXT MILE.</span>
          </SectionHeading>
          <div className="relative grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="hidden md:block absolute top-10 left-[12%] right-[12%] h-[2px]">
              <svg width="100%" height="4" preserveAspectRatio="none">
                <line x1="0" y1="2" x2="100%" y2="2" stroke="#D4AF37" strokeWidth="2" strokeDasharray="12 14" className="road-dash" />
              </svg>
            </div>
            <motion.div className="hidden md:flex absolute top-10 -translate-y-1/2 z-10 w-9 h-9 rounded-full nm-gold-bg items-center justify-center shadow-[0_0_20px_rgba(212,175,55,0.6)]"
              initial={{ left: "10%" }} whileInView={{ left: "86%" }} viewport={{ once: false }}
              transition={{ duration: 7, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}>
              <Truck size={16} className="text-[#0B132B]" />
            </motion.div>
            {STEPS.map((st, i) => (
              <Reveal key={st.num} delay={i * 0.12}>
                <div className="relative text-center" data-testid={`step-${st.num}`}>
                  <div className="relative z-10 w-20 h-20 mx-auto rounded-full nm-card border-2 border-[#D4AF37]/40 flex items-center justify-center mb-5">
                    <span className="font-head text-2xl font-black nm-gold-text">{st.num}</span>
                  </div>
                  <h3 className="font-head text-base font-bold text-white mb-2 tracking-wide">{st.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{st.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Marquee items={MARQUEE} />

      {/* ===================== TESTIMONIALS ===================== */}
      <section className="relative py-24 overflow-hidden">
        <BackgroundGrid className="opacity-15" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6">
          <SectionHeading eyebrow="Client stories" center className="mb-12">
            THE <span className="nm-gold-text">NEXT MILE</span> EXPERIENCE
          </SectionHeading>
          {testimonials.length === 0 ? (
            <Reveal>
              <div className="nm-card rounded-2xl p-10 text-center">
                <Quote className="mx-auto text-[#D4AF37] mb-4" size={32} />
                <p className="text-gray-400">Real client stories are coming soon. Your success could be the next one we share.</p>
                <Link to="/contact" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#D4AF37]">
                  Start your journey <ArrowRight size={15} />
                </Link>
              </div>
            </Reveal>
          ) : (
            <div>
              <motion.div key={tIndex} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                className="nm-card rounded-2xl p-8 sm:p-10 text-center" data-testid="testimonial-card">
                <div className="flex justify-center gap-1 mb-5">
                  {[...Array(testimonials[tIndex]?.rating || 5)].map((_, i) => (
                    <Star key={i} size={18} className="fill-[#D4AF37] text-[#D4AF37]" />
                  ))}
                </div>
                <p className="text-lg text-gray-200 leading-relaxed italic mb-6">"{testimonials[tIndex]?.text}"</p>
                <div className="flex items-center justify-center gap-3">
                  {testimonials[tIndex]?.photo && <img src={testimonials[tIndex].photo} alt="" className="w-11 h-11 rounded-full object-cover" />}
                  <div className="text-left">
                    <p className="font-bold text-white text-sm">{testimonials[tIndex]?.name}</p>
                    <p className="text-gray-400 text-xs">{testimonials[tIndex]?.company} {testimonials[tIndex]?.service && `· ${testimonials[tIndex].service}`}</p>
                  </div>
                </div>
              </motion.div>
              <div className="flex justify-center gap-2 mt-6">
                {testimonials.map((_, i) => (
                  <button key={i} onClick={() => setTIndex(i)}
                    className={`h-2 rounded-full transition-all ${i === tIndex ? "w-8 nm-gold-bg" : "w-2 bg-white/20"}`}
                    aria-label={`Testimonial ${i + 1}`} />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ===================== FAQ ===================== */}
      <section className="relative py-24 bg-[#0B132B]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <SectionHeading eyebrow="Questions" center className="mb-12">
            FREQUENTLY ASKED <span className="nm-gold-text">QUESTIONS</span>
          </SectionHeading>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <Reveal key={f.id} delay={i * 0.03}>
                <div className="nm-card rounded-xl overflow-hidden" data-testid={`faq-${i}`}>
                  <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                    className="w-full flex items-center justify-between gap-4 p-5 text-left"
                    data-testid={`faq-toggle-${i}`}>
                    <span className="font-semibold text-white text-sm sm:text-base">{f.question}</span>
                    <motion.span animate={{ rotate: openFaq === i ? 45 : 0 }} className="text-[#D4AF37] text-2xl font-light shrink-0">+</motion.span>
                  </button>
                  <motion.div initial={false} animate={{ height: openFaq === i ? "auto" : 0, opacity: openFaq === i ? 1 : 0 }}
                    className="overflow-hidden">
                    <p className="px-5 pb-5 text-gray-400 text-sm leading-relaxed">{f.answer}</p>
                  </motion.div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABand eyebrow="New authority? Need loads? Audit coming?" title="Ready to build your trucking company? Let's start." button="GET STARTED" />
    </div>
  );
}
